import React, { useEffect, useMemo, useState } from "react";
import {
  ClipboardList,
  ExternalLink,
  PackageCheck,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  Save,
  Trash2,
  Truck,
} from "lucide-react";

const EMPTY_FORM = {
  id: 0,
  azienda_id: "",
  customer_name: "",
  customer_email: "",
  customer_phone: "",
  source: "manual",
  status: "requested",
  product_name: "",
  quantity: 1,
  serial_numbers: "",
  external_order_id: "",
  order_number: "",
  ddt_number: "",
  tracking_number: "",
  carrier: "",
  notes: "",
  requested_at: "",
  shipped_at: "",
};

const STATUS_OPTIONS = [
  { value: "requested", label: "Richiesto" },
  { value: "processing", label: "In lavorazione" },
  { value: "shipped", label: "Spedito" },
  { value: "delivered", label: "Consegnato" },
  { value: "cancelled", label: "Annullato" },
];

function apiError(data, fallback) {
  return data?.message || data?.error || fallback;
}

async function getJson(url) {
  const res = await fetch(url, { credentials: "include" });
  const data = await res.json().catch(() => null);
  if (!res.ok || data?.ok === false) throw new Error(apiError(data, "Errore API"));
  return data;
}

async function postJson(url, body) {
  const res = await fetch(url, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => null);
  if (!res.ok || data?.ok === false) throw new Error(apiError(data, "Errore API"));
  return data;
}

function statusLabel(status) {
  return STATUS_OPTIONS.find((item) => item.value === status)?.label || status;
}

function statusClass(status) {
  if (status === "delivered") return "bg-emerald-100 text-emerald-700";
  if (status === "shipped") return "bg-sky-100 text-sky-700";
  if (status === "processing") return "bg-indigo-100 text-indigo-700";
  if (status === "cancelled") return "bg-rose-100 text-rose-700";
  return "bg-amber-100 text-amber-800";
}

function dateInputValue(value) {
  if (!value) return "";
  return String(value).slice(0, 16).replace(" ", "T");
}

function normalizeForSave(form) {
  return {
    ...form,
    azienda_id: form.azienda_id ? Number(form.azienda_id) : "",
    quantity: Math.max(1, Number(form.quantity || 1)),
    requested_at: form.requested_at ? form.requested_at.replace("T", " ") : "",
    shipped_at: form.shipped_at ? form.shipped_at.replace("T", " ") : "",
  };
}

export default function HardwareOrdersAdmin({ isSuper = false }) {
  const [items, setItems] = useState([]);
  const [aziende, setAziende] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [status, setStatus] = useState("all");
  const [source, setSource] = useState("all");
  const [query, setQuery] = useState("");

  async function loadOrders() {
    setLoading(true);
    setErr("");
    try {
      const params = new URLSearchParams();
      params.set("status", status);
      params.set("source", source);
      if (query.trim()) params.set("q", query.trim());
      const data = await getJson(`/api/admin/hardware_orders_list.php?${params.toString()}`);
      setItems(Array.isArray(data.items) ? data.items : []);
    } catch (e) {
      setErr(e.message || "Errore caricamento ordini");
    } finally {
      setLoading(false);
    }
  }

  async function loadAziende() {
    try {
      const data = await getJson("/api/admin/aziende_list.php");
      const rows = Array.isArray(data) ? data : data.items || [];
      setAziende(rows);
      if (!isSuper && rows.length === 1) {
        setForm((prev) => ({ ...prev, azienda_id: rows[0].id }));
      }
    } catch (e) {
      setAziende([]);
    }
  }

  useEffect(() => {
    loadAziende();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, source]);

  const stats = useMemo(() => {
    return items.reduce(
      (acc, item) => {
        acc.total += 1;
        if (item.status === "requested") acc.requested += 1;
        if (item.status === "shipped") acc.shipped += 1;
        if (item.status === "delivered") acc.delivered += 1;
        return acc;
      },
      { total: 0, requested: 0, shipped: 0, delivered: 0 }
    );
  }, [items]);

  function resetForm() {
    const defaultCompany = !isSuper && aziende.length === 1 ? aziende[0].id : "";
    setForm({ ...EMPTY_FORM, azienda_id: defaultCompany });
    setErr("");
    setMsg("");
  }

  function editItem(item) {
    setForm({
      id: Number(item.id || 0),
      azienda_id: item.azienda_id || "",
      customer_name: item.customer_name || "",
      customer_email: item.customer_email || "",
      customer_phone: item.customer_phone || "",
      source: item.source || "manual",
      status: item.status || "requested",
      product_name: item.product_name || "",
      quantity: Number(item.quantity || 1),
      serial_numbers: item.serial_numbers || "",
      external_order_id: item.external_order_id || "",
      order_number: item.order_number || "",
      ddt_number: item.ddt_number || "",
      tracking_number: item.tracking_number || "",
      carrier: item.carrier || "",
      notes: item.notes || "",
      requested_at: dateInputValue(item.requested_at),
      shipped_at: dateInputValue(item.shipped_at),
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
    setErr("");
    setMsg("");
  }

  async function saveOrder(e) {
    e.preventDefault();
    setSaving(true);
    setErr("");
    setMsg("");
    try {
      await postJson("/api/admin/hardware_orders_save.php", normalizeForSave(form));
      setMsg(form.id ? "Ordine hardware aggiornato." : "Ordine hardware creato.");
      resetForm();
      await loadOrders();
    } catch (e) {
      setErr(e.message || "Errore salvataggio");
    } finally {
      setSaving(false);
    }
  }

  async function deleteOrder(id) {
    if (!window.confirm("Eliminare questo ordine hardware?")) return;
    setErr("");
    setMsg("");
    try {
      await postJson("/api/admin/hardware_orders_delete.php", { id });
      setMsg("Ordine eliminato.");
      if (Number(form.id) === Number(id)) resetForm();
      await loadOrders();
    } catch (e) {
      setErr(e.message || "Errore eliminazione");
    }
  }

  return (
    <div className="space-y-5">
      <div className="rounded-2xl border border-slate-200 bg-white p-4 md:p-6 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-2xl font-semibold text-slate-900">
              <ClipboardList size={22} />
              Ordini hardware
            </div>
            <div className="text-sm text-slate-500">
              Richieste manuali e acquisti esterni con matricole, tracking, ordine e DDT.
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 text-sm md:grid-cols-4">
            <div className="rounded-xl bg-slate-100 px-3 py-2 text-slate-700">
              <div className="text-xs">Totali</div>
              <div className="font-semibold">{stats.total}</div>
            </div>
            <div className="rounded-xl bg-amber-50 px-3 py-2 text-amber-800">
              <div className="text-xs">Richiesti</div>
              <div className="font-semibold">{stats.requested}</div>
            </div>
            <div className="rounded-xl bg-sky-50 px-3 py-2 text-sky-700">
              <div className="text-xs">Spediti</div>
              <div className="font-semibold">{stats.shipped}</div>
            </div>
            <div className="rounded-xl bg-emerald-50 px-3 py-2 text-emerald-700">
              <div className="text-xs">Consegnati</div>
              <div className="font-semibold">{stats.delivered}</div>
            </div>
          </div>
        </div>

        {msg && (
          <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
            {msg}
          </div>
        )}
        {err && (
          <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {err}
          </div>
        )}

        <form onSubmit={saveOrder} className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-12">
          <div className="md:col-span-4">
            <label className="text-xs font-medium text-slate-600">Azienda</label>
            <select
              value={form.azienda_id}
              onChange={(e) => setForm({ ...form, azienda_id: e.target.value })}
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2.5 outline-none focus:border-slate-500"
              required
              disabled={!isSuper && aziende.length === 1}
            >
              <option value="">Seleziona azienda</option>
              {aziende.map((azienda) => (
                <option key={azienda.id} value={azienda.id}>
                  {azienda.nome}
                </option>
              ))}
            </select>
          </div>

          <div className="md:col-span-4">
            <label className="text-xs font-medium text-slate-600">Cliente / referente</label>
            <input
              value={form.customer_name}
              onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2.5 outline-none focus:border-slate-500"
              required
            />
          </div>

          <div className="md:col-span-2">
            <label className="text-xs font-medium text-slate-600">Origine</label>
            <select
              value={form.source}
              onChange={(e) => setForm({ ...form, source: e.target.value })}
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2.5 outline-none focus:border-slate-500"
            >
              <option value="manual">Manuale</option>
              <option value="external">Acquisto esterno</option>
            </select>
          </div>

          <div className="md:col-span-2">
            <label className="text-xs font-medium text-slate-600">Stato</label>
            <select
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value })}
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2.5 outline-none focus:border-slate-500"
            >
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="md:col-span-4">
            <label className="text-xs font-medium text-slate-600">Email</label>
            <input
              type="email"
              value={form.customer_email}
              onChange={(e) => setForm({ ...form, customer_email: e.target.value })}
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2.5 outline-none focus:border-slate-500"
            />
          </div>

          <div className="md:col-span-3">
            <label className="text-xs font-medium text-slate-600">Telefono</label>
            <input
              value={form.customer_phone}
              onChange={(e) => setForm({ ...form, customer_phone: e.target.value })}
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2.5 outline-none focus:border-slate-500"
            />
          </div>

          <div className="md:col-span-4">
            <label className="text-xs font-medium text-slate-600">Prodotto hardware</label>
            <input
              value={form.product_name}
              onChange={(e) => setForm({ ...form, product_name: e.target.value })}
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2.5 outline-none focus:border-slate-500"
              placeholder="Sonda, gateway, stampante..."
              required
            />
          </div>

          <div className="md:col-span-1">
            <label className="text-xs font-medium text-slate-600">Q.ta</label>
            <input
              type="number"
              min="1"
              value={form.quantity}
              onChange={(e) => setForm({ ...form, quantity: Number(e.target.value || 1) })}
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2.5 outline-none focus:border-slate-500"
            />
          </div>

          <div className="md:col-span-12">
            <label className="text-xs font-medium text-slate-600">Matricole assegnate</label>
            <textarea
              value={form.serial_numbers}
              onChange={(e) => setForm({ ...form, serial_numbers: e.target.value })}
              className="mt-1 min-h-[72px] w-full rounded-xl border border-slate-200 px-3 py-2.5 outline-none focus:border-slate-500"
              placeholder="Una matricola per riga oppure separate da virgola"
            />
          </div>

          <div className="md:col-span-3">
            <label className="text-xs font-medium text-slate-600">ID acquisto esterno</label>
            <input
              value={form.external_order_id}
              onChange={(e) => setForm({ ...form, external_order_id: e.target.value })}
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2.5 outline-none focus:border-slate-500"
            />
          </div>

          <div className="md:col-span-3">
            <label className="text-xs font-medium text-slate-600">Numero ordine</label>
            <input
              value={form.order_number}
              onChange={(e) => setForm({ ...form, order_number: e.target.value })}
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2.5 outline-none focus:border-slate-500"
            />
          </div>

          <div className="md:col-span-3">
            <label className="text-xs font-medium text-slate-600">DDT</label>
            <input
              value={form.ddt_number}
              onChange={(e) => setForm({ ...form, ddt_number: e.target.value })}
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2.5 outline-none focus:border-slate-500"
            />
          </div>

          <div className="md:col-span-3">
            <label className="text-xs font-medium text-slate-600">Tracking number</label>
            <input
              value={form.tracking_number}
              onChange={(e) => setForm({ ...form, tracking_number: e.target.value })}
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2.5 outline-none focus:border-slate-500"
            />
          </div>

          <div className="md:col-span-3">
            <label className="text-xs font-medium text-slate-600">Corriere</label>
            <input
              value={form.carrier}
              onChange={(e) => setForm({ ...form, carrier: e.target.value })}
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2.5 outline-none focus:border-slate-500"
            />
          </div>

          <div className="md:col-span-3">
            <label className="text-xs font-medium text-slate-600">Richiesto il</label>
            <input
              type="datetime-local"
              value={form.requested_at}
              onChange={(e) => setForm({ ...form, requested_at: e.target.value })}
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2.5 outline-none focus:border-slate-500"
            />
          </div>

          <div className="md:col-span-3">
            <label className="text-xs font-medium text-slate-600">Spedito il</label>
            <input
              type="datetime-local"
              value={form.shipped_at}
              onChange={(e) => setForm({ ...form, shipped_at: e.target.value })}
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2.5 outline-none focus:border-slate-500"
            />
          </div>

          <div className="md:col-span-12">
            <label className="text-xs font-medium text-slate-600">Note operative</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="mt-1 min-h-[72px] w-full rounded-xl border border-slate-200 px-3 py-2.5 outline-none focus:border-slate-500"
            />
          </div>

          <div className="md:col-span-12 flex flex-wrap gap-2">
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-60"
            >
              {form.id ? <Save size={16} /> : <Plus size={16} />}
              {saving ? "Salvataggio..." : form.id ? "Aggiorna ordine" : "Crea ordine"}
            </button>
            <button
              type="button"
              onClick={resetForm}
              className="rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Nuovo
            </button>
          </div>
        </form>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-4 md:p-6 shadow-sm">
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[170px]">
            <label className="text-xs font-medium text-slate-600">Stato</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2"
            >
              <option value="all">Tutti</option>
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div className="min-w-[170px]">
            <label className="text-xs font-medium text-slate-600">Origine</label>
            <select
              value={source}
              onChange={(e) => setSource(e.target.value)}
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2"
            >
              <option value="all">Tutte</option>
              <option value="manual">Manuale</option>
              <option value="external">Acquisto esterno</option>
            </select>
          </div>
          <div className="min-w-[260px] flex-1">
            <label className="text-xs font-medium text-slate-600">Cerca</label>
            <div className="mt-1 flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2">
              <Search size={16} className="text-slate-400" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") loadOrders();
                }}
                placeholder="Cliente, azienda, matricola, ordine, DDT, tracking..."
                className="w-full outline-none"
              />
            </div>
          </div>
          <button
            onClick={loadOrders}
            className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-indigo-700"
          >
            <RefreshCw size={16} />
            Aggiorna
          </button>
        </div>

        <div className="mt-5">
          {loading ? (
            <div className="rounded-xl border border-slate-200 p-5 text-slate-500">Caricamento...</div>
          ) : items.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-200 p-5 text-slate-500">
              Nessun ordine hardware trovato.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-slate-500">
                    <th className="px-3 py-3">Ordine</th>
                    <th className="px-3 py-3">Cliente</th>
                    <th className="px-3 py-3">Hardware</th>
                    <th className="px-3 py-3">Matricole</th>
                    <th className="px-3 py-3">Spedizione</th>
                    <th className="px-3 py-3">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={item.id} className="border-b border-slate-100 align-top">
                      <td className="px-3 py-3">
                        <div className="font-semibold text-slate-900">#{item.id}</div>
                        <div className="mt-1 flex flex-wrap gap-1">
                          <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusClass(item.status)}`}>
                            {statusLabel(item.status)}
                          </span>
                          <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
                            {item.source === "external" ? <ExternalLink size={12} /> : <Pencil size={12} />}
                            {item.source === "external" ? "Esterno" : "Manuale"}
                          </span>
                        </div>
                        <div className="mt-1 text-xs text-slate-500">{item.requested_at}</div>
                      </td>
                      <td className="px-3 py-3">
                        <div className="font-medium text-slate-800">{item.customer_name}</div>
                        <div className="text-xs text-slate-500">{item.azienda_nome || `Azienda #${item.azienda_id}`}</div>
                        {item.customer_email ? <div className="text-xs text-slate-500">{item.customer_email}</div> : null}
                        {item.customer_phone ? <div className="text-xs text-slate-500">{item.customer_phone}</div> : null}
                      </td>
                      <td className="px-3 py-3">
                        <div className="inline-flex items-center gap-2 font-medium text-slate-800">
                          <PackageCheck size={15} />
                          {item.product_name}
                        </div>
                        <div className="text-xs text-slate-500">Quantita: {item.quantity}</div>
                        {item.external_order_id ? (
                          <div className="text-xs text-slate-500">Ext: {item.external_order_id}</div>
                        ) : null}
                        {item.order_number ? <div className="text-xs text-slate-500">Ordine: {item.order_number}</div> : null}
                        {item.ddt_number ? <div className="text-xs text-slate-500">DDT: {item.ddt_number}</div> : null}
                      </td>
                      <td className="px-3 py-3">
                        {item.serial_numbers ? (
                          <div className="max-w-[240px] whitespace-pre-wrap rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-700">
                            {item.serial_numbers}
                          </div>
                        ) : (
                          <span className="text-slate-400">Da assegnare</span>
                        )}
                      </td>
                      <td className="px-3 py-3">
                        {item.tracking_number || item.carrier ? (
                          <div className="inline-flex items-start gap-2 text-slate-700">
                            <Truck size={15} className="mt-0.5" />
                            <div>
                              {item.carrier ? <div className="font-medium">{item.carrier}</div> : null}
                              {item.tracking_number ? <div>{item.tracking_number}</div> : null}
                              {item.shipped_at ? <div className="text-xs text-slate-500">{item.shipped_at}</div> : null}
                            </div>
                          </div>
                        ) : (
                          <span className="text-slate-400">Non spedito</span>
                        )}
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex flex-wrap gap-2">
                          <button
                            onClick={() => editItem(item)}
                            className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-3 py-2 text-slate-700 hover:bg-slate-50"
                          >
                            <Pencil size={14} />
                            Modifica
                          </button>
                          <button
                            onClick={() => deleteOrder(item.id)}
                            className="inline-flex items-center gap-1 rounded-lg border border-rose-300 px-3 py-2 text-rose-700 hover:bg-rose-50"
                          >
                            <Trash2 size={14} />
                            Elimina
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
